import React, { useCallback, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { UserProfile } from '../../../AccountPage/components/new/popups/userData.types';
import { coerceUserProfile } from '../../../AccountPage/components/new/popups/userData.types';
import { formatRuPhoneParensFromE164 } from '../../../AccountPage/components/new/popups/PersonalDataPopupContent';

import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../../../../../store/store';
import { setUser } from '../../../../../../store/AuthSlice';
import { getCsrfToken } from '../../../../../../utils/helpers';

import SidePopup from '../../../../../shared/SidePopup/SidePopup';
import AuthPopupContent from '../../../../../shared/Header/authPopup/AuthPopupContent';
import PersonalDataPopupContent from '../../../AccountPage/components/new/popups/PersonalDataPopupContent';
import './recipientSection.scss';

type FormValue = Record<string, string>;
type FormErrors = Record<string, string | undefined>;
type Props = {
    authUser: UserProfile | null | undefined;
    isAuthenticated: boolean;
    userDataMissing: boolean;
    onCtaClick: () => void;
    highlight?: boolean;

    // этап 1: эти пропсы НЕ обязательны
    value?: FormValue;
    errors?: FormErrors;
    onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
};
export type RecipientSectionHandle = {
    ensureProfileForCheckout: () => Promise<UserProfile | null>;
    openAuthPopup: () => void;
};

function pickString(obj: unknown, key: string): string {
    if (!obj || typeof obj !== 'object') return '';
    const v = (obj as Record<string, unknown>)[key];
    return typeof v === 'string' ? v : '';
}
function pickBoolean(obj: unknown, key: string, defaultValue = false): boolean {
    if (!obj || typeof obj !== 'object') return defaultValue;
    const v = (obj as Record<string, unknown>)[key];
    return typeof v === 'boolean' ? v : defaultValue;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESEND_COOLDOWN_MS = 5 * 60 * 1000;
const nowMs = () => Date.now();
const normalizeSpaces = (v: string): string =>
    String(v || '')
        .replace(/\s+/g, ' ')
        .trim();

const normEmailKey = (email: string) =>
    String(email || '')
        .trim()
        .toLowerCase();
const keyLastSent = (email: string) => `email_confirm_last_sent_at:${normEmailKey(email)}`;

function safeReadLastSentAt(email: string): number | null {
    try {
        const raw = localStorage.getItem(keyLastSent(email));
        if (!raw) return null;
        const n = Number(raw);
        return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
        return null;
    }
}
function safeWriteLastSentAt(email: string, ts: number) {
    try {
        localStorage.setItem(keyLastSent(email), String(ts));
    } catch {
        // ignore
    }
}
function calcRemainingMs(lastSentAt: number | null, now: number): number {
    if (!lastSentAt) return 0;
    const left = RESEND_COOLDOWN_MS - (now - lastSentAt);
    return left > 0 ? left : 0;
}
function fmtMMSS(ms: number): string {
    const totalSec = Math.ceil(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

type ProfileMode = 'guest' | 'missing' | 'partial' | 'ready';
type EmailStep = 'email' | 'code';
type NetPhase = 'idle' | 'saving' | 'sending' | 'confirming';
type FieldKey = 'firstName' | 'lastName' | 'email' | 'code' | 'common';

function validateFirstName(v: string): string | null {
    const s = normalizeSpaces(v);
    if (!s) return 'Укажите имя';
    if (s.length > 25) return 'Имя слишком длинное';
    return null;
}
function validateLastName(v: string): string | null {
    const s = normalizeSpaces(v);
    if (!s) return 'Укажите фамилию';
    if (s.length > 55) return 'Фамилия слишком длинная';
    return null;
}
function validateEmailOptional(v: string): string | null {
    const s = normalizeSpaces(v);
    if (!s) return null;
    if (s.length > 55) return 'E-mail слишком длинный';
    if (!EMAIL_RE.test(s)) return 'Некорректный e-mail';
    return null;
}
function validateEmailRequired(v: string): string | null {
    const s = normalizeSpaces(v);
    if (!s) return 'Укажите e-mail';
    return validateEmailOptional(s);
}

function normalizeUserAddressLikeLegacy(u: UserProfile): UserProfile {
    const anyU = u as any;
    let changed = false;
    const next: any = { ...anyU };
    const hasContainer = next.address && typeof next.address === 'object' && !Array.isArray(next.address);
    const hasLegacyStruct = !!next.addressStruct;
    const hasLegacyRaw = typeof next.addressRaw === 'string' && next.addressRaw.trim().length > 0;

    if (hasLegacyStruct || hasLegacyRaw) {
        const struct = hasLegacyStruct ? next.addressStruct : undefined;
        const raw = hasLegacyRaw ? String(next.addressRaw).trim() : undefined;

        if (struct || raw) {
            next.address = {
                raw: raw || (hasContainer ? next.address.raw : ''),
                struct: struct || (hasContainer ? next.address.struct : null),
            };
            changed = true;
        } else {
            next.address = null;
            changed = true;
        }

        if (hasLegacyStruct) {
            delete next.addressStruct;
            changed = true;
        }
        if (next.addressRaw !== undefined) {
            delete next.addressRaw;
            changed = true;
        }
    }

    if (hasContainer) {
        const raw = typeof next.address.raw === 'string' ? next.address.raw.trim() : '';
        const struct = next.address.struct && typeof next.address.struct === 'object' ? next.address.struct : null;
        if (!raw && !struct) {
            next.address = null;
            changed = true;
        } else {
            next.address = { raw: raw || '', struct };
            changed = true;
        }
    }

    return changed ? (next as UserProfile) : u;
}

function extractUserFromAnyPayload(payload: unknown): UserProfile {
    const maybeUser = payload && typeof payload === 'object' && 'user' in (payload as any) ? (payload as any).user : payload;
    return coerceUserProfile(maybeUser) as unknown as UserProfile;
}

function mergeAddresses(fromPrev: UserProfile | null | undefined, next: UserProfile): UserProfile {
    const prevAddr = Array.isArray((fromPrev as any)?.addresses) ? (fromPrev as any).addresses : [];
    const nextHas = Array.isArray((next as any)?.addresses);
    if (nextHas) return next;
    (next as any).addresses = prevAddr;
    return next;
}

export const RecipientSection = forwardRef<RecipientSectionHandle, Props>(function RecipientSection(
    { authUser, isAuthenticated, userDataMissing, onCtaClick, highlight = false },
    ref,
) {
    const dispatch = useDispatch<AppDispatch>();

    const [authPopupOpen, setAuthPopupOpen] = useState(false);
    const openAuthPopup = useCallback(() => setAuthPopupOpen(true), []);
    const closeAuthPopup = useCallback(() => setAuthPopupOpen(false), []);

    const [personalPopupOpen, setPersonalPopupOpen] = useState(false);
    const openPersonal = () => setPersonalPopupOpen(true);
    const closePersonal = () => setPersonalPopupOpen(false);

    const serverFirstName = useMemo(() => pickString(authUser, 'firstName'), [authUser]);
    const serverLastName = useMemo(() => pickString(authUser, 'lastName'), [authUser]);
    const serverMiddleName = useMemo(() => pickString(authUser, 'middleName'), [authUser]);
    const phone = useMemo(() => pickString(authUser, 'phoneNumber'), [authUser]);
    const serverEmail = useMemo(() => pickString(authUser, 'email'), [authUser]);
    const emailStatus = useMemo(() => pickBoolean(authUser, 'emailStatus'), [authUser]);

    const [firstNameInput, setFirstNameInput] = useState('');
    const [lastNameInput, setLastNameInput] = useState('');
    const [emailInput, setEmailInput] = useState('');
    const [emailCode, setEmailCode] = useState('');
    const [emailStep, setEmailStep] = useState<EmailStep>('email');
    const [netPhase, setNetPhase] = useState<NetPhase>('idle');
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey, string>>>({});

    const abortRef = useRef<AbortController | null>(null);
    const emailLockedRef = useRef<string>('');

    const [lastSentAt, setLastSentAt] = useState<number | null>(() => (serverEmail ? safeReadLastSentAt(serverEmail) : null));
    const [nowTick, setNowTick] = useState(() => nowMs());
    const tickRef = useRef<number | null>(null);

    const leftMs = useMemo(() => calcRemainingMs(lastSentAt, nowTick), [lastSentAt, nowTick]);
    const canResend = leftMs <= 0;
    const busy = netPhase !== 'idle';

    const setErr = useCallback((key: FieldKey, msg: string) => {
        setFieldErrors((prev) => ({ ...prev, [key]: msg }));
    }, []);
    const clearErr = useCallback((key: FieldKey) => {
        setFieldErrors((prev) => {
            if (!prev[key]) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);

    useEffect(() => {
        if (!authUser) return;

        setFirstNameInput(serverFirstName);
        setLastNameInput(serverLastName);
        setEmailInput(serverEmail);

        if (emailStatus) {
            setEmailStep('email');
            setEmailCode('');
            emailLockedRef.current = '';
            setFieldErrors({});
        } else {
            if (emailStep === 'code' && emailLockedRef.current && normEmailKey(emailLockedRef.current) !== normEmailKey(serverEmail)) {
                setEmailStep('email');
                setEmailCode('');
                emailLockedRef.current = '';
            }
        }

        if (serverEmail) {
            setLastSentAt(safeReadLastSentAt(serverEmail));
            setNowTick(nowMs());
        } else {
            setLastSentAt(null);
            setNowTick(nowMs());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authUser, serverFirstName, serverLastName, serverEmail, emailStatus]);

    useEffect(() => {
        const shouldTick = !!serverEmail && calcRemainingMs(lastSentAt, nowMs()) > 0;
        if (!shouldTick) {
            if (tickRef.current) {
                window.clearInterval(tickRef.current);
                tickRef.current = null;
            }
            return;
        }
        if (!tickRef.current) {
            tickRef.current = window.setInterval(() => setNowTick(nowMs()), 1000);
        }
        return () => {
            if (tickRef.current) {
                window.clearInterval(tickRef.current);
                tickRef.current = null;
            }
        };
    }, [serverEmail, lastSentAt]);

    useEffect(() => {
        return () => {
            abortRef.current?.abort();
            if (tickRef.current) {
                window.clearInterval(tickRef.current);
                tickRef.current = null;
            }
        };
    }, []);

    const effectiveFirstName = useMemo(() => normalizeSpaces(serverFirstName || firstNameInput), [serverFirstName, firstNameInput]);
    const effectiveLastName = useMemo(() => normalizeSpaces(serverLastName || lastNameInput), [serverLastName, lastNameInput]);
    const effectiveEmail = useMemo(() => normalizeSpaces(serverEmail || emailInput), [serverEmail, emailInput]);

    const mode: ProfileMode = useMemo(() => {
        if (!isAuthenticated) return 'guest';
        const fnErr = validateFirstName(effectiveFirstName);
        const lnErr = validateLastName(effectiveLastName);
        const phoneMissing = !normalizeSpaces(phone);
        const missingRequired = !!fnErr || !!lnErr || phoneMissing;
        if (missingRequired) return 'missing';
        return emailStatus && !!effectiveEmail ? 'ready' : 'partial';
    }, [isAuthenticated, effectiveFirstName, effectiveLastName, effectiveEmail, emailStatus, phone]);

    const hintHighlight = highlight && (mode === 'guest' || mode === 'missing');

    const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFirstNameInput(e.target.value);
        clearErr('firstName');
        clearErr('common');
    };
    const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLastNameInput(e.target.value);
        clearErr('lastName');
        clearErr('common');
    };
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmailInput(e.target.value);
        clearErr('email');
        clearErr('common');
    };

    const handleFirstNameBlur = () => {
        if (serverFirstName) return;
        const err = validateFirstName(firstNameInput);
        if (err) setErr('firstName', err);
    };
    const handleLastNameBlur = () => {
        if (serverLastName) return;
        const err = validateLastName(lastNameInput);
        if (err) setErr('lastName', err);
    };
    const handleEmailBlur = () => {
        if (emailStep !== 'email') return;
        const err = validateEmailOptional(emailInput || serverEmail);
        if (err) setErr('email', err);
    };

    const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmailCode(e.target.value);
        clearErr('code');
        clearErr('common');
    };

    const patchProfileIfNeeded = useCallback(
        async (options?: { requireEmail?: boolean }): Promise<UserProfile> => {
            if (!authUser) throw new Error('NO_USER');
            const requireEmail = Boolean(options?.requireEmail);
            const fnErr = validateFirstName(effectiveFirstName);
            const lnErr = validateLastName(effectiveLastName);
            const emErr = requireEmail ? validateEmailRequired(effectiveEmail) : validateEmailOptional(effectiveEmail);
            setFieldErrors((prev) => {
                const next = { ...prev };
                if (fnErr) next.firstName = fnErr;
                if (lnErr) next.lastName = lnErr;
                if (emErr) next.email = emErr;
                return next;
            });
            if (fnErr || lnErr || emErr) throw new Error('VALIDATION');
            const normalizedUser = normalizeUserAddressLikeLegacy(authUser);
            const nextEmail = normalizeSpaces(effectiveEmail);
            const shouldPatch =
                normalizeSpaces(serverFirstName) !== effectiveFirstName ||
                normalizeSpaces(serverLastName) !== effectiveLastName ||
                normalizeSpaces(serverEmail) !== nextEmail;
            if (!shouldPatch) return normalizedUser;
            abortRef.current?.abort();
            const ctrl = new AbortController();
            abortRef.current = ctrl;
            setNetPhase('saving');
            clearErr('common');
            const csrf = await getCsrfToken();
            const body: {
                lastName: string;
                firstName: string;
                middleName: string;
                address: UserProfile['address'] | null;
                email?: string;
            } = {
                lastName: effectiveLastName,
                firstName: effectiveFirstName,
                middleName: normalizeSpaces(serverMiddleName),
                address: (normalizedUser.address ?? null) as UserProfile['address'] | null,
            };
            if (nextEmail) body.email = nextEmail;
            const r = await fetch(`${process.env.REACT_APP_SERVER_URL}/auth/user`, {
                method: 'PATCH',
                credentials: 'include',
                signal: ctrl.signal,
                headers: {
                    'Content-Type': 'application/json',
                    'csrf-token': csrf,
                },
                body: JSON.stringify(body),
            });
            const data = (await r.json().catch(() => ({}))) as unknown;
            if (!r.ok) {
                const o: any = data;
                const msg = typeof o?.error === 'string' ? String(o.error) : typeof o?.message === 'string' ? String(o.message) : 'Не удалось сохранить данные';
                throw new Error(msg);
            }
            const next = mergeAddresses(authUser, extractUserFromAnyPayload(data));
            dispatch(setUser(next));
            return next;
        },
        [authUser, dispatch, effectiveEmail, effectiveFirstName, effectiveLastName, serverEmail, serverFirstName, serverLastName, serverMiddleName, clearErr],
    );

    const requestEmailCode = useCallback(async () => {
        abortRef.current?.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;

        setNetPhase('sending');
        clearErr('common');

        try {
            const csrf = await getCsrfToken();
            const r = await fetch(`${process.env.REACT_APP_SERVER_URL}/auth/request-email-code`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'csrf-token': csrf },
                signal: ctrl.signal,
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                const msg = typeof (data as any)?.message === 'string' ? String((data as any).message) : 'Не удалось отправить код подтверждения.';
                throw new Error(msg);
            }

            const ts = nowMs();
            safeWriteLastSentAt(effectiveEmail, ts);
            setLastSentAt(ts);
            setNowTick(ts);

            setEmailStep('code');
            emailLockedRef.current = effectiveEmail;
            setErr('common', 'Код отправлен. Введите его из письма.');
        } finally {
            setNetPhase('idle');
        }
    }, [effectiveEmail, clearErr, setErr]);

    const confirmEmail = useCallback(
        async (code: string) => {
            const v = String(code || '').trim();
            if (!v) {
                setErr('code', 'Введите код подтверждения');
                return;
            }

            abortRef.current?.abort();
            const ctrl = new AbortController();
            abortRef.current = ctrl;

            setNetPhase('confirming');
            clearErr('common');

            try {
                const csrf = await getCsrfToken();
                const emailForConfirm = emailLockedRef.current || effectiveEmail;

                const r = await fetch(`${process.env.REACT_APP_SERVER_URL}/auth/confirm-email`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json', 'csrf-token': csrf },
                    body: JSON.stringify({ code: v, email: emailForConfirm }),
                    signal: ctrl.signal,
                });

                const data = (await r.json().catch(() => ({}))) as unknown;
                if (!r.ok) {
                    const msg = typeof (data as any)?.message === 'string' ? String((data as any).message) : 'Ошибка подтверждения.';
                    throw new Error(msg);
                }

                const next = mergeAddresses(authUser, extractUserFromAnyPayload(data));
                dispatch(setUser(next));

                setEmailStep('email');
                setEmailCode('');
                emailLockedRef.current = '';
                setFieldErrors({});
            } finally {
                setNetPhase('idle');
            }
        },
        [authUser, dispatch, effectiveEmail, clearErr, setErr],
    );

    const handleEmailActionClick = useCallback(async () => {
        if (!isAuthenticated) return;
        if (!authUser) return;
        if (emailStatus) return;
        clearErr('common');
        try {
            if (emailStep === 'email') {
                const fnErr = serverFirstName ? null : validateFirstName(firstNameInput);
                const lnErr = serverLastName ? null : validateLastName(lastNameInput);
                const emErr = validateEmailRequired(effectiveEmail);
                if (fnErr) setErr('firstName', fnErr);
                if (lnErr) setErr('lastName', lnErr);
                if (emErr) setErr('email', emErr);
                if (fnErr || lnErr || emErr) return;
                await patchProfileIfNeeded({ requireEmail: true });
                if (!canResend) {
                    setErr('common', `Повторно через ${fmtMMSS(leftMs)}`);
                    return;
                }
                await requestEmailCode();
                return;
            }

            // emailStep === 'code'
            const trimmed = String(emailCode || '').trim();
            if (!trimmed) {
                if (!canResend) {
                    setErr('common', `Повторно через ${fmtMMSS(leftMs)}`);
                    return;
                }
                await requestEmailCode();
                return;
            }

            await confirmEmail(trimmed);
        } catch (e: any) {
            if (e?.name === 'AbortError') return;
            const msg = typeof e?.message === 'string' && e.message ? e.message : 'Ошибка сети/сервера';
            setErr('common', msg);
        }
    }, [
        isAuthenticated,
        authUser,
        emailStatus,
        emailStep,
        emailCode,
        serverFirstName,
        serverLastName,
        firstNameInput,
        lastNameInput,
        effectiveEmail,
        patchProfileIfNeeded,
        requestEmailCode,
        confirmEmail,
        canResend,
        leftMs,
        clearErr,
        setErr,
    ]);

    const emailDisplayValue = useMemo(() => {
        if (!effectiveEmail) return 'Не указан';
        if (emailStatus) return effectiveEmail;
        return `${effectiveEmail} (не подтверждён)`;
    }, [effectiveEmail, emailStatus]);

    const emailHintText = useMemo(() => {
        if (fieldErrors.common) return fieldErrors.common;
        if (fieldErrors.email && emailStep === 'email') return fieldErrors.email;
        if (fieldErrors.code && emailStep === 'code') return fieldErrors.code;
        if (emailStep === 'code') return 'Введите код из письма. Если нужно - очистите поле и нажмите кнопку для переотправки.';
        return 'Для получения статуса заказа и информации об оплате (необязательно).';
    }, [fieldErrors.common, fieldErrors.email, fieldErrors.code, emailStep]);

    useImperativeHandle(
        ref,
        () => ({
            openAuthPopup,
            ensureProfileForCheckout: async () => {
                if (!isAuthenticated || !authUser) return null;
                clearErr('common');
                try {
                    return await patchProfileIfNeeded({ requireEmail: false });
                } catch (e: any) {
                    if (e?.name === 'AbortError') throw e;
                    if (typeof e?.message === 'string' && e.message && e.message !== 'VALIDATION') {
                        setErr('common', e.message);
                    }
                    throw e;
                }
            },
        }),
        [openAuthPopup, isAuthenticated, authUser, patchProfileIfNeeded, clearErr, setErr],
    );

    const sectionRef = useRef<HTMLElement | null>(null);
    return (
        <section ref={sectionRef} className="recipientSection" aria-label="Получатель">
            <div className="recipientSection__header">
                <div className="recipientSection__header_wrapper">
                    <h3 className="recipientSection__title">Получатель</h3>
                    {authUser && firstNameInput && (
                        <button type="button" className="recipientSection__edit" title="Изменить данные" onClick={openPersonal}>
                            Изменить
                        </button>
                    )}
                </div>

                {mode === 'guest' ? (
                    <div className="recipientSection__wrapper">
                        <p className={`recipientSection__hint ${hintHighlight ? 'recipientSection__hint--highlight' : ''}`}>
                            Войдите в аккаунт для быстрого оформления заказа.
                        </p>
                        <div className="recipientSection__guestForm">
                            <button className="recipientSection__guestForm_button" type="button" onClick={openAuthPopup}>
                                Войти
                            </button>
                        </div>
                    </div>
                ) : null}

                {mode === 'missing' ? (
                    <p className={`recipientSection__hint ${hintHighlight ? 'recipientSection__hint--highlight' : ''}`}>
                        Заполните личные данные для продолжения оформления заказа.
                    </p>
                ) : null}

                {mode === 'partial' ? (
                    <p className="recipientSection__hint">Данные заполнены. Можно оформить заказ. E-mail и его подтверждение - по желанию.</p>
                ) : null}
            </div>

            <div className="recipientSection__body">
                {(mode === 'missing' || mode === 'partial' || mode === 'ready') && (
                    <div className="recipientSection__guestForm">
                        <div className="recipientSection__guestForm_wrapper recipientSection__guestForm_wrapper--two">
                            <div className="recipientSection__guestForm_valueBox">
                                {serverFirstName ? (
                                    <span className="recipientSection__guestForm_value">{serverFirstName}</span>
                                ) : (
                                    <input
                                        className="recipientSection__guestForm_input"
                                        placeholder="Имя*"
                                        value={firstNameInput}
                                        onChange={handleFirstNameChange}
                                        onBlur={handleFirstNameBlur}
                                        aria-invalid={fieldErrors.firstName ? true : undefined}
                                        disabled={busy}
                                    />
                                )}
                                {fieldErrors.firstName ? <span className="recipientSection__guestForm_input_hint">{fieldErrors.firstName}</span> : null}
                            </div>
                            <div className="recipientSection__guestForm_valueBox">
                                {serverLastName ? (
                                    <span className="recipientSection__guestForm_value">{serverLastName}</span>
                                ) : (
                                    <input
                                        className="recipientSection__guestForm_input"
                                        placeholder="Фамилия*"
                                        value={lastNameInput}
                                        onChange={handleLastNameChange}
                                        onBlur={handleLastNameBlur}
                                        aria-invalid={fieldErrors.lastName ? true : undefined}
                                        disabled={busy}
                                    />
                                )}
                                {fieldErrors.lastName ? <span className="recipientSection__guestForm_input_hint">{fieldErrors.lastName}</span> : null}
                            </div>
                        </div>

                        <div className="recipientSection__guestForm_wrapper recipientSection__guestForm_wrapper--two">
                            <span className="recipientSection__guestForm_value">{phone ? formatRuPhoneParensFromE164(phone) : '-'}</span>
                            {effectiveEmail && <span className="recipientSection__guestForm_value">{emailDisplayValue}</span>}
                        </div>

                        {!emailStatus && (
                            <div className="recipientSection__guestForm_wrapper">
                                <div className="recipientSection__guestForm_valueBox">
                                    {emailStep === 'email' ? (
                                        serverEmail ? (
                                            <span className="recipientSection__guestForm_value">{emailInput}</span>
                                        ) : (
                                            <input
                                                className="recipientSection__guestForm_input"
                                                placeholder="Электронная почта"
                                                inputMode="email"
                                                autoComplete="email"
                                                value={emailInput}
                                                onChange={handleEmailChange}
                                                onBlur={handleEmailBlur}
                                                aria-invalid={fieldErrors.email ? true : undefined}
                                                disabled={busy}
                                            />
                                        )
                                    ) : (
                                        <input
                                            className="recipientSection__guestForm_input"
                                            placeholder="Код"
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                            value={emailCode}
                                            onChange={handleCodeChange}
                                            aria-invalid={fieldErrors.code ? true : undefined}
                                            disabled={busy}
                                        />
                                    )}
                                    <span className="recipientSection__guestForm_input_hint">{emailHintText}</span>
                                </div>

                                <div className="recipientSection__guestForm_valueBox">
                                    <button
                                        className="recipientSection__guestForm_button"
                                        type="button"
                                        onClick={handleEmailActionClick}
                                        disabled={
                                            busy ||
                                            !isAuthenticated ||
                                            (emailStep === 'email' && !canResend) ||
                                            (emailStep === 'code' && !emailCode.trim() && !canResend)
                                        }
                                    >
                                        {busy && netPhase === 'saving' ? 'Сохраняем…' : null}
                                        {busy && netPhase === 'sending' ? 'Отправляем…' : null}
                                        {busy && netPhase === 'confirming' ? 'Проверяем…' : null}
                                        {!busy && emailStep === 'email' ? 'Подтвердить' : null}
                                        {!busy && emailStep === 'code' && emailCode.trim() ? 'Подтвердить' : null}
                                        {!busy && emailStep === 'code' && !emailCode.trim() && canResend ? 'Отправить ещё раз' : null}
                                        {!busy && emailStep === 'code' && !emailCode.trim() && !canResend ? `Повторить через ${fmtMMSS(leftMs)}` : null}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {!authUser ? (
                <SidePopup open={authPopupOpen} onClose={closeAuthPopup} className="headerAuthPopup" showCloseButton={false}>
                    <AuthPopupContent onClose={closeAuthPopup} />
                </SidePopup>
            ) : (
                <SidePopup open={personalPopupOpen} onClose={closePersonal} className="myDataPanel__personalSidePopup" showCloseButton={false}>
                    <PersonalDataPopupContent user={authUser} onClose={closePersonal} />
                </SidePopup>
            )}
        </section>
    );
});
